import React from 'react'
import { Box, Card, CardContent, Grid, Skeleton } from '@mui/material'
import { CARD_DIMENSIONS } from '@/housefolios-components/Property/PropertyCard/constants'

import { LIST_GRID_MIN_HEIGHT } from '@/housefolios-components/Property/PropertyList/listGridHeight'

/**
 * Skeleton placeholders that match PropertyCard / CompsCard grid sizing
 * while property lists are loading.
 *
 * @param {'property'|'comps'} variant
 *   property — My Properties / Marketplace / Trash card grids
 *   comps — sale & rental comps card grids (lg=6, xl=4)
 */
export function PropertyCardSkeleton({
  count = 8,
  isPublic = false,
  fullWidth = false,
  variant = 'property',
}) {
  const isComps = variant === 'comps'

  return (
    <>
      {Array.from({ length: count  }).map((_, index) => (
        <Grid
          key={`property-card-skeleton-${index}`}
          size={{
            xs: 12,
            ...(isComps
              ? { lg: 6, xl: fullWidth ? 12 : 4 }
              : {
                  md: fullWidth ? 12 : isPublic ? 4 : 6,
                  xl: fullWidth ? 12 : isPublic ? 4 : 3,
                }),
          }}
          sx={
            isComps
              ? [{ marginBottom: "24px" }, { display: "flex" }, { width: "100%" }]
              : { marginBottom: "24px" }
          }
        >
          <Card sx={[{
            position: "relative",
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
            backgroundColor: "#fff",
            borderRadius: "0.65rem",
            boxShadow: "0 0.46875rem 2.1875rem rgba(59, 62, 102, 0.03), 0 0.9375rem 1.40625rem rgba(59, 62, 102, 0.03), 0 0.25rem 0.53125rem rgba(59, 62, 102, 0.05), 0 0.125rem 0.1875rem rgba(59, 62, 102, 0.03)",
            "button&": { outline: "none" },
            wordWrap: "break-word",
            backgroundClip: "border-box",
            border: "0 solid rgba(79, 80, 98, 0.3)",
            "html[data-color-mode=\"dark\"] &": { backgroundColor: "#1e1e1e !important", color: "rgba(255, 255, 255, 0.87)" },
          }, { backgroundColor: 'white', height: '100%', width: isComps ? '100%' : undefined, }, {
            transition: "all 0.2s ease-in-out !important",
            "&:hover": { boxShadow: "0 0.56875rem 3.3875rem rgba(59, 62, 102, 0.05), 0 0.9975rem 2.40625rem rgba(59, 62, 102, 0.07), 0 0.35rem 1.23125rem rgba(59, 62, 102, 0.05), 0 0.225rem 1.1875rem rgba(59, 62, 102, 0.09)", transform: "translateY(-15px)" },
            "&.card-box-hover:hover": { boxShadow: "0 0.56875rem 3.3875rem rgba(59, 62, 102, 0.05), 0 0.9975rem 2.40625rem rgba(59, 62, 102, 0.07), 0 0.35rem 1.23125rem rgba(59, 62, 102, 0.05), 0 0.225rem 1.1875rem rgba(59, 62, 102, 0.09)", transform: "translateY(0)" },
          }]}>
            <Skeleton
              variant="rectangular"
              animation="wave"
              height={CARD_DIMENSIONS.IMAGE_HEIGHT}
              sx={{ width: '100%' }}
            />
            <CardContent>
              <Skeleton
                variant="text"
                animation="wave"
                width="75%"
                height={28}
              />
              <Skeleton
                variant="text"
                animation="wave"
                width="45%"
                height={20}
              />
              <Box sx={{ mt: 2 }}>
                <Skeleton
                  variant="text"
                  animation="wave"
                  width="90%"
                />
                <Skeleton
                  variant="text"
                  animation="wave"
                  width="80%"
                />
                <Skeleton
                  variant="text"
                  animation="wave"
                  width="65%"
                />
              </Box>
              {!isComps && (
                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                  <Skeleton
                    variant="rounded"
                    animation="wave"
                    height={36}
                    sx={{ flex: 1 }}
                  />
                  <Skeleton
                    variant="rounded"
                    animation="wave"
                    height={36}
                    sx={{ flex: 1 }}
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      ))}
    </>
  )
}

/**
 * Skeleton placeholder for property / comps list (table) view.
 */
export function PropertyListSkeleton({ rowCount = 10  }) {
  return (
    <Grid size={12}>
      <Card
        sx={{
          p: 2,
          backgroundColor: 'white',
          width: '100%',
          minHeight: LIST_GRID_MIN_HEIGHT,
        }}
      >
        <Skeleton
          variant="rectangular"
          animation="wave"
          height={48}
          sx={{ mb: 1.5, borderRadius: 1 }}
        />
        {Array.from({ length: rowCount  }).map((_, index) => (
          <Skeleton
            key={`property-list-skeleton-${index}`}
            variant="rectangular"
            animation="wave"
            height={44}
            sx={{ mb: 1, borderRadius: 1 }}
          />
        ))}
      </Card>
    </Grid>
  )
}

export default PropertyCardSkeleton
